// Step 2: the device. A two-probe device as in QuantumATK-like builders: a central (scattering) region of
// `length` cells along the transport direction, the same cross-section repeated across, and semi-infinite leads
// of `lead_cells` cells on both ends, all from the (optionally re-chosen) cell of the fit. The view updates as
// the numbers change, with the size against the plan.
import { Alert, Badge, Button, Grid, Group, Loader, NumberInput, SegmentedControl, Stack, Switch, Text } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { ErrorBox } from "../../components/Errors";
import { Plot } from "../../components/Plot";
import type { Limits, ProjectDetail } from "../../lib/api";
import { det3, deviceFigure, deviceSpec, IDENTITY, negfDevice, transverse, withAxis, type DeviceForm, type NegfMemory } from "../../lib/negf";

const AXIS = ["a1", "a2", "a3"];

export function useDeviceGeometry(project: ProjectDetail, mem: NegfMemory) {
  const spec = useMemo(() => deviceSpec(mem.device), [mem.device]);
  const [debounced] = useDebouncedValue(spec, 300);
  return useQuery({
    queryKey: ["negf-device", mem.model_file, debounced],
    queryFn: () => negfDevice(mem.model_file!, debounced, project.id),
    enabled: !!mem.model_file,
    placeholderData: (prev) => prev,
  });
}

function SupercellEditor({ m, onChange }: { m: number[][]; onChange: (m: number[][]) => void }) {
  const d = det3(m);
  return (
    <Stack gap={4}>
      {m.map((row, i) => (
        <Group key={i} gap={4} wrap="nowrap">
          <Text size="xs" w={70}>new {AXIS[i]} =</Text>
          {row.map((v, j) => (
            <NumberInput key={j} size="xs" w={64} value={v} allowDecimal={false} aria-label={`supercell ${i + 1},${j + 1}`}
              onChange={(x) => onChange(m.map((r, a) => r.map((c, b) => (a === i && b === j ? Number(x) || 0 : c))))} />
          ))}
        </Group>
      ))}
      <Text size="xs" c={d === 0 ? "red" : "dimmed"}>{d === 0 ? "The rows are not independent (determinant 0)."
        : `${Math.abs(d)} cell${Math.abs(d) === 1 ? "" : "s"} of the fit per device cell.`}</Text>
    </Stack>
  );
}

export function DeviceStep({ project, mem, update, remember, limits, onBack, onNext }: {
  project: ProjectDetail; mem: NegfMemory; update: (m: Partial<NegfMemory>) => void; remember: (m: Partial<NegfMemory>) => void;
  limits: Limits; onBack: () => void; onNext: () => void;
}) {
  const d = mem.device;
  const set = (next: Partial<DeviceForm>) => update({ device: { ...d, ...next } });
  const geo = useDeviceGeometry(project, mem);
  const g = geo.data;
  const fig = useMemo(() => (g && !g.errors.length ? deviceFigure(g) : null), [g]);
  const lengths = mem.summary?.lengths ?? [];
  const ok = !!g && !g.errors.length && g.within_limit !== false && (!d.supercell || det3(d.supercell) !== 0);

  return (
    <Grid mt="md" gutter="lg">
      <Grid.Col span={{ base: 12, md: 4 }}>
        <Stack gap="sm">
          <div>
            <Text size="sm" fw={500} mb={4}>Transport direction</Text>
            <SegmentedControl fullWidth value={String(d.transport_axis)} onChange={(v) => update({ device: withAxis(d, Number(v)) })}
              data={AXIS.map((a, i) => ({ value: String(i), label: `${a}${lengths[i] ? ` (${lengths[i].toFixed(2)} Å)` : ""}` }))}
              data-testid="transport-axis" />
          </div>
          <NumberInput label="Central region length" description={`cells along ${AXIS[d.transport_axis]}`} min={1} allowDecimal={false}
            value={d.length} onChange={(v) => set({ length: Math.max(1, Number(v) || 1) })} />
          {transverse(d.transport_axis).map((a) => (
            <NumberInput key={a} label={`Width along ${AXIS[a]}`} description="cells (1 = a single layer / chain)" min={1} allowDecimal={false}
              value={d.cells[String(a)] ?? 1} onChange={(v) => set({ cells: { ...d.cells, [String(a)]: Math.max(1, Number(v) || 1) } })} />
          ))}
          <NumberInput label="Lead unit" description="cells per lead period (more cells for long-range hoppings)" min={1} allowDecimal={false}
            value={d.lead_cells} onChange={(v) => set({ lead_cells: Math.max(1, Number(v) || 1) })} />
          <Switch label="Re-choose the cell (supercell of the fit's cell)" checked={!!d.supercell}
            onChange={(e) => set({ supercell: e.currentTarget.checked ? IDENTITY.map((r) => [...r]) : null })} />
          {d.supercell && <SupercellEditor m={d.supercell} onChange={(m) => set({ supercell: m })} />}
          {d.supercell && <Text size="xs" c="dimmed">E.g. for graphene: rows (1, 2, 0), (1, 0, 0), (0, 0, 1) give a rectangular cell, so a1 is armchair and a2 zigzag.</Text>}
        </Stack>
      </Grid.Col>
      <Grid.Col span={{ base: 12, md: 8 }}>
        <Stack gap="xs">
          {geo.isLoading && <Loader size="sm" />}
          {geo.error && <ErrorBox error={geo.error} />}
          {g?.errors.length ? <Alert color="red" title="The device is not complete">{g.errors.join(" · ")}
            {g.min_lead_cells ? <Button ml="sm" size="compact-xs" variant="light" onClick={() => set({ lead_cells: g.min_lead_cells! })}>
              Set the lead unit to {g.min_lead_cells}</Button> : null}</Alert> : null}
          {g && !g.errors.length && (
            <Group gap="xs" data-testid="device-size">
              <Badge variant="light" color={g.within_limit === false ? "red" : "blue"}>
                {g.orbitals?.toLocaleString()} orbitals{g.limit_orbitals ? ` of ${g.limit_orbitals.toLocaleString()} allowed` : ""}
              </Badge>
              <Badge variant="outline">{g.sites_total?.toLocaleString()} atoms drawn (central region + one lead unit each side)</Badge>
              {g.spin_resolved && <Badge variant="light" color="grape">spin-resolved</Badge>}
            </Group>
          )}
          {g?.within_limit === false && <Alert color="orange">This device is larger than your plan allows
            ({String(limits["negf.max_device_orbitals"])} orbitals). Make it shorter or narrower.</Alert>}
          {g?.truncated && <Text size="xs" c="dimmed">Only the first atoms are drawn (view limit); the calculation uses the whole device.</Text>}
          {fig && <Plot data={fig.data} layout={fig.layout} height={420} />}
        </Stack>
      </Grid.Col>
      <Grid.Col span={12}>
        <Group justify="space-between">
          <Button variant="default" onClick={onBack}>Back</Button>
          <Button disabled={!ok} onClick={() => { remember({ device: d }); onNext(); }}>Next: gates</Button>
        </Group>
      </Grid.Col>
    </Grid>
  );
}
